import type { RouteRecordRaw } from 'vue-router';
import type { App } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';
import { basicRoutes } from './routes';
import { persistEncryption } from '@/store/plugin/persist';
import { getLocalUserMenus } from '@/store/modules/userInfo';

// 白名单应该包含基本静态路由
const WHITE_NAME_LIST: string[] = [];

const getRouteNames = (array: any[]) =>
  array.forEach((item) => {
    WHITE_NAME_LIST.push(item.name);
    getRouteNames(item.children || []);
  });

getRouteNames(basicRoutes);

const NO_PERMISSION_PATH = '/403';

const isPublicPath = (path: string) => {
  return (
    path === '/login' ||
    path === '/login2' ||
    path === '/adlogin' ||
    path === '/auth' ||
    path === '/403' ||
    path.startsWith('/exception')
  );
};

const getUserMenuKeys = () => {
  try {
    return getLocalUserMenus()
      .map((menu) => menu.menuKey)
      .filter((menuKey): menuKey is string => Boolean(menuKey));
  } catch (error) {
    console.log(error);
    return [];
  }
};

const hasRoutePermission = (to) => {
  const requiredPermission = to.meta?.perCode;

  if (!requiredPermission || isPublicPath(to.path)) {
    return true;
  }

  const menuKeys = getUserMenuKeys();

  if (menuKeys.length === 0) {
    return true;
  }

  if (Array.isArray(requiredPermission)) {
    return requiredPermission.some((permission) => menuKeys.includes(permission));
  }

  return menuKeys.includes(requiredPermission);
};

const redirectToNoPermission = (next, to) => {
  next({
    path: NO_PERMISSION_PATH,
    replace: true,
    query: {
      redirect: encodeURIComponent(to.fullPath)
    }
  });
};

// app router
// 创建一个可以被 Vue 应用程序使用的路由实例
export const router = createRouter({
  // 创建一个 hash 历史记录。
  history: createWebHashHistory(import.meta.env.VITE_PUBLIC_PATH),
  // 应该添加到路由的初始路由列表。
  routes: basicRoutes as unknown as RouteRecordRaw[],
  // 是否应该禁止尾部斜杠。默认为假
  strict: true,
  scrollBehavior: () => ({ left: 0, top: 0 })
});

// reset router
export function resetRouter() {
  router.getRoutes().forEach((route) => {
    const { name } = route;
    if (name && !WHITE_NAME_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name);
    }
  });
}

router.beforeEach((to, from, next) => {
  if (import.meta.env.MODE == 'web') {
    if (isPublicPath(to.path)) {
      next();
      return;
    }

    let userInfo: any = null;
    const FLOW__WEB__userInfo = localStorage.getItem('FLOW__WEB__userInfo');
    if (FLOW__WEB__userInfo) {
      try {
        userInfo = JSON.parse(FLOW__WEB__userInfo).token ? JSON.parse(FLOW__WEB__userInfo) : null;
      } catch (e) {
        try {
          const decryptData = persistEncryption.decrypt(FLOW__WEB__userInfo);
          userInfo = JSON.parse(decryptData).token ? JSON.parse(decryptData) : null;
        } catch (e) {
          console.log(e);
          userInfo = true;
        }
      }
    }

    // 处理 login2 路由 - 允许直接访问
    if (
      to.path === '/login2' ||
      from.path === '/login2' ||
      to.path === '/adlogin' ||
      from.path === '/adlogin'
    ) {
      next();
      return;
    }

    // 检查 URL hash 中是否包含 login2，如果是则重定向到 login2
    if (window.location.hash.includes('login2') && to.path !== '/login2') {
      next({ path: '/login2' });
      return;
    }

    // 检查 URL hash 中是否包含 login2，如果是则重定向到 login2
    if (window.location.hash.includes('adlogin') && to.path !== '/adlogin') {
      next({ path: '/adlogin' });
      return;
    }

    if (
      (!from.path || from.path === '/' || from.path === '/login' || !userInfo) &&
      to.path == '/login'
    ) {
      next();
    } else {
      if (!userInfo) {
        if (to.path == '/login') {
          next();
        } else {
          next({
            path: '/login'
          });
        }
      } else {
        const isLoginSuccess = sessionStorage.getItem('isLoginSuccess');
        if (!isLoginSuccess) {
          if (to.path == '/login') {
            next();
          } else {
            next({
              path: '/login'
            });
          }
        } else {
          let allowBack = true;
          if (
            (from.path && to.path == '/login' && isLoginSuccess) ||
            (from.path == '/login' && !isLoginSuccess)
          ) {
            allowBack = false;
          }
          if (!hasRoutePermission(to)) {
            redirectToNoPermission(next, to);
            return;
          }
          next(allowBack);
        }
      }
    }
  } else {
    if (!hasRoutePermission(to)) {
      redirectToNoPermission(next, to);
      return;
    }
    next();
  }
});

// 配置路由器
export function setupRouter(app: App<Element>) {
  app.use(router);
}
